import React, { useState, useRef } from "react";
import useCompanyScore from "./hooks/useCompanyScore";

// Component imports
import AlertModal from "../../components/SATI/AlertModal";
import TeamHealthChart from "./components/Company/TeamHealthChart";
import GlobalScoreCard from "./components/GlobalScoreCard";
import TotalUsersCard from "./components/TotalUsersCard";
import GenderCard from "./components/GenderCard";
import MetricProgressBarCard from "./components/MetricProgressBarCard";
import CompanyUserTable from "./components/Company/CompanyUserTable";

// Hooks
import { useCompanyDashboardData } from "./hooks/useCompanyDashboardData";
import { useNeuroaDashboard } from "./hooks/useNeuroaDashboard";
import GlassmorphicFilters from "./components/GlassmorphicFilters";

// Chart utils
import { getMetricWeeklyArray, calculateDynamicDomain } from "./helpers/chartUtils";

// Neuroa integration
import NeuroaTooltip from "../../components/Neuroa/NeuroaTooltip";
import "../../components/Neuroa/neuroa-side-panel.css";
import { NeuroaLogo } from "../../components/ui/NeuroaLogo";
import { useNeuroaBridge } from "../../context/NeuroaBridgeContext";
import { cn } from "../../utils/cn";

import "chartist/dist/scss/chartist.scss";
import { FiMoon } from "react-icons/fi";
import { TbBrain, TbWalk, TbGenderMale, TbGenderFemale, TbUsers } from "react-icons/tb";

const CompanyDashboard2 = ({ user }) => {
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const dashboardNeuroaLogoRef = useRef(null);

  const { state, actions, derived } = useCompanyDashboardData();
  const { handleNeuroaClick, isSidebarOpen, isNeuroaOpen } = useNeuroaDashboard(
    dashboardNeuroaLogoRef,
  );
  const { neuroaMessage, showNeuroaWelcome } = useNeuroaBridge();

  const handleCustomRangeReport = (pendingRange) => {
    const range = pendingRange ?? state.dateRange;
    const start = range[0].clone().startOf("day");
    const end   = range[1].clone().endOf("day");
    if (start.isAfter(end, "day")) {
      setShowAlertModal(true);
      return;
    }
    actions.setCustom([start, end]);
  };

  // ── Company Health Score ────────────────────────────────────────────────────
  const {
    data: companyScore,
    scoreDelta,
    loading: companyScoreLoading,
    error: companyScoreError,
  } = useCompanyScore(state.dateRange, state.selectedDisease);

  // ── Demographic data ───────────────────────────────────────────────────────
  const poblationalData = state.poblationalData;
  const total       = poblationalData?.total       ?? 0;
  const with_data   = poblationalData?.with_data   ?? 0;
  const total_men   = poblationalData?.total_men   ?? 0;
  const total_women = poblationalData?.total_women ?? 0;

  // ── Latest record from chartData ───────────────────────────────────────────
  const latestData = React.useMemo(() => {
    if (!state.chartData || state.chartData.length === 0) {
      return state.cardsData || {};
    }
    const sorted = [...state.chartData].sort((a, b) => {
      const dateA = a.date ? new Date(a.date).getTime() : 0;
      const dateB = b.date ? new Date(b.date).getTime() : 0;
      return dateB - dateA;
    });
    return { ...state.cardsData, ...sorted[0] };
  }, [state.chartData, state.cardsData]);

  // ── Radar & Global Score ───────────────────────────────────────────────────
  const radarValues = companyScore
    ? [
        (companyScore.Avg_Score_Stress   ?? 0) / 100, // Estrés
        (companyScore.Avg_Score_Pasos    ?? 0) / 100, // Pasos
        (companyScore.Score_Consistencia ?? 0) / 100, // Consistencia
        (companyScore.Avg_Score_Sueno    ?? 0) / 100, // Sueño
      ]
    : [0, 0, 0, 0];

  const globalScore = companyScore
    ? parseFloat((companyScore.KPI_Final ?? 0).toFixed(2))
    : null;

  // ── Metric card values ─────────────────────────────────────────────────────
  const stressValRaw      = latestData.StressAvgAVG ?? 52;
  const stressDisplayVal  = stressValRaw.toFixed(2);
  const stressBadgeText   =
    stressValRaw <= 26 ? "Descansado"
    : stressValRaw <= 51 ? "Bajo"
    : stressValRaw <= 76 ? "Medio"
    : "Alto";
  const stressProgressPercent = Math.round(stressValRaw);

  const stepsValRaw       = latestData.StepsAVG ?? 8200;
  const stepsDisplayVal   = stepsValRaw.toFixed(2);
  const stepsBadgeText    = stepsValRaw >= 8000 ? "Saludable" : "Bajo";
  const stepsProgressPercent = Math.min(100, Math.round((stepsValRaw / 10000) * 100));

  const sleepValRaw    = latestData.SleepHoursAVG ?? 5.4;
  const sleepHours     = Math.floor(sleepValRaw);
  let   sleepMinutes   = Math.round((sleepValRaw - sleepHours) * 60);
  let   finalHours     = sleepHours;
  if (sleepMinutes === 60) { finalHours += 1; sleepMinutes = 0; }
  const sleepDisplayVal    = finalHours.toString();
  const sleepUnitVal       = `h ${sleepMinutes}m`;
  const sleepBadgeText     = sleepValRaw >= 7 ? "Saludable" : sleepValRaw >= 6 ? "Medio" : "Bajo";
  const sleepProgressPercent = Math.min(100, Math.round((sleepValRaw / 8) * 100));

  const scrollToDetail = () => {
    const el = document.getElementById("detail-section");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  // ── Weekly chart data (stress, steps, sleep) ───────────────────────────────
  const stressChartData = React.useMemo(() => {
    const s1 = getMetricWeeklyArray(state.prevChartData, "StressAvgAVG", 0, 100);
    const s2 = getMetricWeeklyArray(state.chartData,     "StressAvgAVG", 0, 100);
    const { domainMin, domainMax } = calculateDynamicDomain(s1, s2, 0, 100);
    return {
      s1, s2,
      color1: "#94A3B844", color2: "#EF4444",
      domainMin, domainMax,
      tickFn:    (v) => `${Math.round(v)}%`,
      tooltipFn: (v) => `${v.toFixed(2)}`,
    };
  }, [state.chartData, state.prevChartData]);

  const stepsChartData = React.useMemo(() => {
    const s1 = getMetricWeeklyArray(state.prevChartData, "StepsAVG", 0, 12000);
    const s2 = getMetricWeeklyArray(state.chartData,     "StepsAVG", 0, 12000);
    const { domainMin, domainMax } = calculateDynamicDomain(s1, s2, 0, 12000);
    return {
      s1, s2,
      color1: "#94A3B844", color2: "#10B981",
      domainMin, domainMax,
      tickFn:    (v) => v >= 1000 ? `${Math.round(v / 1000)}k` : `${Math.round(v)}`,
      tooltipFn: (v) => `${Math.round(v).toLocaleString("es-MX")}`,
    };
  }, [state.chartData, state.prevChartData]);

  const sleepChartData = React.useMemo(() => {
    const s1 = getMetricWeeklyArray(state.prevChartData, "SleepHoursAVG", 0, 10);
    const s2 = getMetricWeeklyArray(state.chartData,     "SleepHoursAVG", 0, 10);
    const { domainMin, domainMax } = calculateDynamicDomain(s1, s2, 0, 10);
    return {
      s1, s2,
      color1: "#94A3B844", color2: "#6366F1",
      domainMin, domainMax,
      tickFn: (v) => `${Math.round(v)}h`,
      tooltipFn: (v) => {
        const h = Math.floor(v);
        let m = Math.round((v - h) * 60);
        let fh = h;
        if (m === 60) { fh += 1; m = 0; }
        return m > 0 ? `${fh}h ${m}m` : `${fh}h`;
      },
    };
  }, [state.chartData, state.prevChartData]);

  return (
    <React.Fragment>
      <div className="dashboard-content min-h-screen p-6 font-sans">
        <div className="container-fluid max-w-10xl mx-auto space-y-4">

          {/* Filters toggle */}
          <div className="flex justify-between items-center flex-wrap gap-4 pb-2 relative">
            <div className="ml-auto">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold rounded-xl shadow-sm transition-all"
              >
                <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <span>Filtros</span>
              </button>
            </div>

            {showFilters && (
              <div className="absolute right-0 top-full mt-2 z-[100] w-full max-w-xl">
                <GlassmorphicFilters
                  loadingBtn={state.loadingBtn}
                  dateRange={state.dateRange}
                  useMockData={state.useMockData}
                  setUseMockData={actions.setUseMockData}
                  onWeeklyReport={actions.setWeekly}
                  onMonthlyReport={actions.setMonthly}
                  onCustomRangeReport={handleCustomRangeReport}
                  areaData={state.areaData}
                  selectedArea={state.selectedArea}
                  setSelectedArea={actions.setSelectedArea}
                  diseaseData={state.diseases}
                  selectedDisease={state.selectedDisease}
                  setSelectedDisease={actions.setSelectedDisease}
                />
              </div>
            )}
          </div>

          {/* Active filter breadcrumb */}
          {(state.selectedArea || state.selectedUser || state.selectedDisease) && (
            <div className="flex items-center gap-2 p-3.5 bg-blue-50 border border-blue-100 rounded-xl text-sm text-blue-800">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>
                Filtrando por{" "}
                {state.selectedUser
                  ? `Usuario ID: ${state.selectedUser}`
                  : state.selectedArea
                    ? `Área: ${state.selectedArea.name || state.selectedArea.Nombre || state.selectedArea.id}`
                    : `Enfermedad: ${state.selectedDisease}`}
              </span>
              <button
                onClick={() => {
                  actions.setSelectedUser(undefined);
                  actions.setSelectedArea(undefined);
                  actions.setSelectedDisease("");
                }}
                className="underline font-semibold hover:text-blue-900 ml-auto"
              >
                Limpiar selección
              </button>
            </div>
          )}

          {/* ROW 1: Global Score + Demographics */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <div className="lg:col-span-2">
              <GlobalScoreCard
                globalScore={globalScore}
                monthlyImprovement={scoreDelta}
                onScrollToDetail={scrollToDetail}
                radarValues={radarValues}
                loading={companyScoreLoading}
                error={!!companyScoreError}
              />
            </div>

            <div className="lg:col-span-3 h-full">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 h-full">
                <TotalUsersCard
                  total={total}
                  ageData={{
                    age_18_25: (poblationalData?.men_age_18_25 ?? 0) + (poblationalData?.women_age_18_25 ?? 0),
                    age_26_35: (poblationalData?.men_age_26_35 ?? 0) + (poblationalData?.women_age_26_35 ?? 0),
                    age_36_45: (poblationalData?.men_age_36_45 ?? 0) + (poblationalData?.women_age_36_45 ?? 0),
                    age_46_55: (poblationalData?.men_age_46_55 ?? 0) + (poblationalData?.women_age_46_55 ?? 0),
                    age_55_plus: (poblationalData?.men_age_55_plus ?? 0) + (poblationalData?.women_age_55_plus ?? 0),
                  }}
                  icon={<TbUsers className="w-5 h-5" />}
                />
                <GenderCard
                  title="Hombres"
                  count={total_men}
                  total={total}
                  withData={with_data}
                  icon={<TbGenderMale className="w-5 h-5" />}
                />
                <GenderCard
                  title="Mujeres"
                  count={total_women}
                  total={total}
                  withData={with_data}
                  icon={<TbGenderFemale className="w-5 h-5" />}
                  isRose={true}
                />
              </div>
            </div>
          </div>

          {/* ROW 2: Metric cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
            <MetricProgressBarCard
              title="Nivel de estrés"
              numberVal={stressDisplayVal}
              unitVal="/100"
              badgeText={stressBadgeText}
              progressPercent={stressProgressPercent}
              icon={<TbBrain className="w-5 h-5" />}
              chartData={stressChartData}
              dateRange={state.dateRange}
            />
            <MetricProgressBarCard
              title="Pasos"
              numberVal={stepsDisplayVal}
              unitVal=""
              badgeText={stepsBadgeText}
              progressPercent={stepsProgressPercent}
              icon={<TbWalk className="w-5 h-5" />}
              chartData={stepsChartData}
              dateRange={state.dateRange}
            />
            <MetricProgressBarCard
              title="Sueño"
              numberVal={sleepDisplayVal}
              unitVal={sleepUnitVal}
              badgeText={sleepBadgeText}
              progressPercent={sleepProgressPercent}
              icon={<FiMoon className="w-5 h-5" />}
              chartData={sleepChartData}
              dateRange={state.dateRange}
            />
          </div>

          {/* ROW 3: Team Health Chart */}
          <div id="detail-section">
            <TeamHealthChart data={state.chartData} />
          </div>

          {/* ROW 4: Colaboradores Table */}
          {derived?.userTableData && (
            <div className="pt-2">
              <CompanyUserTable
                tableData={derived.userTableData}
                userData={state.userData}
                selectedUserId={state.selectedUser}
                onRegisterData={actions.recordManualHealthData}
                canRegisterData={!state.useMockData}
              />
            </div>
          )}

          <AlertModal
            show={showAlertModal}
            toggle={() => setShowAlertModal(!showAlertModal)}
          >
            El rango de meses debería ser válido!
          </AlertModal>
        </div>

        {/* Floating Neuroa button */}
        <div
          className={cn(
            "fixed bottom-8 right-8 z-50 transition-all duration-500",
            isSidebarOpen && !isNeuroaOpen
              ? "scale-100 opacity-100 pointer-events-auto"
              : "scale-0 opacity-0 pointer-events-none",
          )}
        >
          <NeuroaTooltip
            text={neuroaMessage}
            forceShow={showNeuroaWelcome && !isNeuroaOpen}
          >
            <button
              onClick={handleNeuroaClick}
              onMouseEnter={() => dashboardNeuroaLogoRef.current?.setHover(true)}
              onMouseLeave={() => dashboardNeuroaLogoRef.current?.setHover(false)}
              className="w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:scale-110 active:scale-95 transition-all duration-300 group cursor-pointer overflow-hidden border border-gray-100"
            >
              <NeuroaLogo ref={dashboardNeuroaLogoRef} className="w-8 h-8" />
            </button>
          </NeuroaTooltip>
        </div>
      </div>
    </React.Fragment>
  );
};

export default CompanyDashboard2;
